import React from "react";
import { User, Hash, Phone, Mail, MapPin, Tag, CreditCard, Building, CheckCircle2, FileText } from "lucide-react";
import { FormInput, FormSelect, SearchableSelect, FormTextarea } from "./SharedFormComponents";
import { INDIAN_STATES } from "../../../../constants/globalConstants";

export const SupplierForm = ({ form, errors, handleInputChange }) => {
  const gstTypes = [
    { value: "CGST", label: "CGST" },
    { value: "SGST", label: "SGST" },
    { value: "IGST", label: "IGST" },
    { value: "UTGST", label: "UTGST" },
    { value: "Others", label: "Others" },
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="space-y-4">
        <h3 className="text-[11px] font-black text-brand-blue uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
          <User size={14} /> Core Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            icon={<User size={14} />}
            label="Vendor Name"
            required
            field="vendorName"
            value={form.vendorName}
            onChange={handleInputChange}
            error={errors.vendorName}
            placeholder="Enter vendor name"
          />
          <FormInput
            icon={<Hash size={14} />}
            label="Vendor Code"
            field="vendorCode"
            value={form.vendorCode}
            onChange={handleInputChange}
            error={errors.vendorCode}
            placeholder="VEN-001"
          />
          <FormInput
            icon={<Phone size={14} />}
            label="Mobile"
            required
            field="mobile"
            value={form.mobile}
            onChange={handleInputChange}
            error={errors.mobile}
            placeholder="9876543210"
          />
          <FormInput
            icon={<Mail size={14} />}
            label="Email"
            field="email"
            type="email"
            value={form.email}
            onChange={handleInputChange}
            error={errors.email}
            placeholder="vendor@example.com"
          />
          <div className="md:col-span-2">
            <FormTextarea
              icon={<MapPin size={14} />}
              label="Address Line 1"
              required
              field="addressLine1"
              value={form.addressLine1}
              onChange={handleInputChange}
              error={errors.addressLine1}
              placeholder="Building name, street..."
            />
          </div>
          <div className="md:col-span-2">
            <FormTextarea
              icon={<MapPin size={14} />}
              label="Address Line 2 (Optional)"
              field="addressLine2"
              value={form.addressLine2}
              onChange={handleInputChange}
              error={errors.addressLine2}
              placeholder="Area, Landmark..."
            />
          </div>

          <SearchableSelect
            icon={<MapPin size={14} />}
            label="State"
            required
            field="state"
            options={INDIAN_STATES.map((s) => ({ value: s, label: s }))}
            value={form.state || ""}
            onChange={handleInputChange}
            error={errors.state}
            placeholder="Select State"
          />
          <FormInput
            icon={<Building size={14} />}
            label="City"
            required
            field="city"
            value={form.city}
            onChange={handleInputChange}
            error={errors.city}
            placeholder="Enter city"
          />
          <FormInput
            icon={<Hash size={14} />}
            label="Pincode"
            required
            field="pincode"
            value={form.pincode}
            onChange={handleInputChange}
            error={errors.pincode}
            placeholder="123456"
          />
          <FormSelect
            icon={<CheckCircle2 size={14} />}
            label="Status"
            required
            field="status"
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "INACTIVE", label: "Inactive" },
            ]}
            value={form.status || "ACTIVE"}
            onChange={handleInputChange}
            error={errors.status}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-[11px] font-black text-brand-blue uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
          <FileText size={14} /> Business & GST Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SearchableSelect
            icon={<Tag size={14} />}
            label="GST Type"
            field="gstType"
            options={gstTypes}
            value={form.gstType}
            onChange={handleInputChange}
            error={errors.gstType}
            placeholder="Select GST Type"
          />
          <FormInput
            icon={<Hash size={14} />}
            label="GST No"
            field="gstNo"
            value={form.gstNo}
            onChange={handleInputChange}
            error={errors.gstNo}
            placeholder="22AAAAA0000A1Z5"
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-[11px] font-black text-brand-blue uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
          <CreditCard size={14} /> Bank Account Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            icon={<User size={14} />}
            label="Account Holder Name"
            field="accountHolderName"
            value={form.accountHolderName}
            onChange={handleInputChange}
            placeholder="Enter holder name"
          />
          <FormInput
            icon={<CreditCard size={14} />}
            label="Account No"
            field="accountNumber"
            value={form.accountNumber}
            onChange={handleInputChange}
            error={errors.accountNumber}
            placeholder="50100000000000"
          />
          <FormInput
            icon={<Building size={14} />}
            label="Bank Name"
            field="bankName"
            value={form.bankName}
            onChange={handleInputChange}
            placeholder="HDFC Bank"
          />
          <FormInput
            icon={<Hash size={14} />}
            label="IFSC Code"
            field="ifscCode"
            value={form.ifscCode}
            onChange={handleInputChange}
            error={errors.ifscCode}
            placeholder="HDFC0000123"
          />
          <FormInput
            icon={<Building size={14} />}
            label="Branch Name"
            field="branchName"
            value={form.branchName}
            onChange={handleInputChange}
            placeholder="Main Branch"
          />
          <div className="md:col-span-2">
            <FormTextarea
              icon={<FileText size={14} />}
              label="Description"
              field="description"
              value={form.description}
              onChange={handleInputChange}
              error={errors.description}
              placeholder="Additional vendor notes..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};
